import CommonDatePicker from "@/common/elements/DatePicker";
import Checkbox from "@/common/elements/InputElements/CheckBox";
import Select from "@/common/elements/InputElements/Select";
import TextField from "@/common/elements/InputElements/TextField";
import {
  StyledFlex,
  StyledGridCol,
  StyledGridRow,
  StyledSectionHeading,
} from "@/common/styles";
import {
  InmateDetailsTableContainer,
  TableHeader,
} from "../InmateDetails/styles";
import Button from "@/common/elements/Button";
import Table from "@/common/elements/Table";
import Modal from "@/common/elements/Modal";
import { FieldSecurityProvider } from "@/contexts/FieldSecurityContext";
import SecureFormItem from "@/common/SecureFormItem";
import { useEffect, useState, useMemo, useRef, useCallback } from "react";
import {
  checkGlobalClientParams,
  debounce,
  getOptionsFromRefList,
  hasCheckSecurity,
  isNonEmptyVariable,
  convertApiFields,
} from "@/util/common_function";
import { useDispatch, useSelector } from "react-redux";
import { Form, Popconfirm } from "antd";
import dayjs from "dayjs";
import { setBookings, setDeleteAPis } from "@/slices/bookingsSlice";
import { v4 as uuidv4 } from "uuid";
import api from "@/services/axios_custom";
import API_ENDPOINTS from "@/util/api";
import { Entity_types, Security_Types } from "@/util/permission_bits";
import { LABELS_CAPITAL } from "@/util/usePermissionMask";
import {
  MODULES_LIST_WITH_ID,
  SCHEMA_NAME,
  SUB_MODULES_LIST_WITH_ID,
} from "@/util/common_variable";
import { removeInJailFlag } from "@/components/BookingsDetails/JacketDetails/flagUtils";
import useGridColumnConfig from "@/hooks/useGridColumnConfig";

const RELEASE_NOTES_HEADERS = [
  { key: "releaseNote", text: "Release Note" },
  { key: "cleared", text: "Cleared" },
  { key: "comments", text: "Comments" },
];

// ─── helpers ────────────────────────────────────────────────────────────────

/**
 * Build the flat form values the main <Form> expects.
 *
 * Returns:
 *   formValues          — object passed to form.setFieldsValue()
 *   chargesDerivedDate  — raw "MM/DD/YYYY" string when date came from charges,
 *                         null otherwise (used to sync back into arrestReports)
 *   chargesDerivedTime  — matching time string, or null
 */
const buildFormData = (booking, apiFields) => {
  if (!booking)
    return {
      formValues: null,
      chargesDerivedDate: null,
      chargesDerivedTime: null,
      chargesDerivedReleaseReasonId: null,
    };

  const ar = booking?.arrestReports?.[0];
  const stsUse = Boolean(ar?.stsUse);
  const stsDays = isNonEmptyVariable(ar?.stsDays) ? Number(ar.stsDays) : 0;
  const scheduledReleaseOverridden = Boolean(ar?.scheduledReleaseOverridden);
  const toRefListId = (v) =>
    isNonEmptyVariable(v) && !isNaN(v) ? Number(v) : v;

  // Mirrors legacy's STSChanged()/CalculateAllChargeReleaseDates: STS credit
  // is subtracted off the release date as the LAST step, on top of whichever
  // base date won below — never persisted as its own compounding
  // adjustment. No-op when STS isn't in use, there's no credit yet, or the
  // date is manually overridden (matches CalculateOutDate's immediate exit
  // on Scheduled_Release_Overridden).
  const applySTSCredit = (baseDate, baseTime) => {
    if (!stsUse || !stsDays || scheduledReleaseOverridden || !baseDate) {
      return { date: baseDate, time: baseTime };
    }
    return {
      date: dayjs(baseDate, "MM/DD/YYYY").subtract(stsDays, "day").format("MM/DD/YYYY"),
      time: baseTime,
    };
  };

  // ── Scheduled release source resolution ─────────────────────────────────
  // Priority 1: the MAX Scheduled_Release_Date/Time across every sentenced,
  //   non-cleared charge (only when NOT overridden) — mirrors legacy's
  //   CalculateAllChargeReleaseDates, which rolls the booking's release date
  //   up from every charge, not just the one marked "Controlling Charge"
  //   (a charge can be controlling for booking/legal purposes while
  //   carrying a shorter sentence than another charge on the same booking).
  // Priority 2: the Controlling Charge once it has been cleared (Clearance
  //   Information filled in and saved — e.g. "Case Dismissed") — its
  //   Clearance Date/Time and Clearance Reason roll straight up into this
  //   tab's Scheduled Release Date/Time and Release Reason, the moment the
  //   charge is saved, without needing the separate "Release Inmate" action.
  //   Mutually exclusive with Priority 1 by construction: that filter only
  //   considers charges with clearedToGo !== true.
  // Priority 3: value already stored in arrestReports[0]
  let releaseDateTime = null;
  let chargesDerivedDate = null; // only set when Priority 1 or 2 wins
  let chargesDerivedTime = null;
  let chargesDerivedReleaseReasonId = null; // only set when Priority 2 wins

  if (booking?.charges?.length > 0 && !ar?.scheduledReleaseOverridden) {
    const eligible = booking.charges.filter(
      (c) =>
        c.sentenced === true &&
        c.clearedToGo !== true &&
        isNonEmptyVariable(c?.scheduledReleaseDate),
    );
    const candidate = eligible.reduce((max, c) => {
      if (!max) return c;
      const maxDt = dayjs(
        `${max.scheduledReleaseDate} ${max.scheduledReleaseTime || ""}`,
      );
      const cDt = dayjs(
        `${c.scheduledReleaseDate} ${c.scheduledReleaseTime || ""}`,
      );
      return cDt.isAfter(maxDt) ? c : max;
    }, null);
    if (candidate) {
      chargesDerivedDate = candidate.scheduledReleaseDate;
      chargesDerivedTime = candidate.scheduledReleaseTime || null;

      // STS credit is applied fresh from the charge's own (always-current)
      // date for DISPLAY only — chargesDerivedDate/Time below (what gets
      // synced back into arrestReports) stay the raw, pre-credit values, so
      // this can never compound across renders/saves.
      const withCredit = applySTSCredit(chargesDerivedDate, chargesDerivedTime);
      releaseDateTime = dayjs(`${withCredit.date} ${withCredit.time || ""}`);

      // If arrestReports already holds the same (raw) value there is
      // nothing new to write back — clear the "derived" markers so the
      // caller skips dispatch.
      if (
        ar?.scheduledReleaseDate === chargesDerivedDate &&
        (ar?.scheduledReleaseTime || null) === chargesDerivedTime
      ) {
        chargesDerivedDate = null;
        chargesDerivedTime = null;
      }
    }
  }

  // Priority 2 — cleared Controlling Charge. Only considered when Priority 1
  // found nothing (an active sentence on any charge always wins over a
  // dismissal on the controlling one) and the date isn't overridden.
  if (!releaseDateTime && !ar?.scheduledReleaseOverridden) {
    const clearedControlling = booking?.charges?.find(
      (c) => c.controllingCharge === true && c.clearedToGo === true,
    );
    if (
      clearedControlling &&
      isNonEmptyVariable(clearedControlling?.clearanceDate)
    ) {
      const candidateDate = clearedControlling.clearanceDate;
      const candidateTime = clearedControlling.clearanceTime || null;
      releaseDateTime = dayjs(`${candidateDate} ${candidateTime || ""}`);

      // Same one-time write-back semantics as Priority 1 — only flag it as
      // "derived" (for the caller to sync into arrestReports) when it
      // actually differs from what's already stored there.
      if (
        ar?.scheduledReleaseDate !== candidateDate ||
        (ar?.scheduledReleaseTime || null) !== candidateTime
      ) {
        chargesDerivedDate = candidateDate;
        chargesDerivedTime = candidateTime;
      }

      if (
        isNonEmptyVariable(clearedControlling?.clearanceReasonID) &&
        toRefListId(ar?.releaseReasonId) !==
          toRefListId(clearedControlling.clearanceReasonID)
      ) {
        chargesDerivedReleaseReasonId = clearedControlling.clearanceReasonID;
      }
    }
  }

  if (!releaseDateTime && isNonEmptyVariable(ar?.scheduledReleaseDate)) {
    // No controlling/sentenced charge — arrestReports.scheduledReleaseDate
    // is the only source, and (see dataModify's STS block) is maintained
    // there as the already-credit-adjusted live value directly, the same
    // single field legacy uses — so no further subtraction here, just
    // display what's stored.
    releaseDateTime = dayjs(
      `${ar.scheduledReleaseDate} ${ar.scheduledReleaseTime || ""}`,
    );
  }

  // ── Dynamic extra fields ─────────────────────────────────────────────────
  const extraInitialValues = {};
  apiFields?.forEach((field) => {
    const key = field.columnName;
    const raw = ar?.arrestReportsExtra?.[0]?.[key];
    extraInitialValues[key] =
      raw && key.toLowerCase().includes("date")
        ? dayjs(raw).isValid()
          ? dayjs(raw)
          : null
        : raw;
  });

  const formValues = {
    ...extraInitialValues,
    scheduledReleaseOverridden: ar?.scheduledReleaseOverridden,
    releaseOfficerStaffId: ar?.releaseOfficerStaffId,
    releaseReasonId: toRefListId(
      chargesDerivedReleaseReasonId ?? ar?.releaseReasonId,
    ),
    totalDaysInCustody: ar?.totalDaysInCustody,
    // Sentence-To-Serve credit — legacy BookingsInterface.asp "Use STS?" /
    // divSTSInformation. stsUse gates visibility of stsDays/stsHours; those
    // two are read-only here unless the client also runs UseSTS_Manual, in
    // which case staff enter them directly instead of Work Crews accruing
    // them automatically.
    stsUse: ar?.stsUse ?? false,
    stsDays: ar?.stsDays,
    stsHours: ar?.stsHours,
    scheduledReleaseDateTime: releaseDateTime,
    releasedDateTime: isNonEmptyVariable(ar?.releaseDate)
      ? dayjs(`${ar.releaseDate || ""} ${ar.releaseTime || ""}`)
      : null,
    releaseToAuthorityId: toRefListId(ar?.releaseToAuthorityId),
    releaseToFacilityId: toRefListId(ar?.releaseToFacilityId),
    releaseToState: ar?.releaseToState,
  };

  return {
    formValues,
    chargesDerivedDate,
    chargesDerivedTime,
    chargesDerivedReleaseReasonId,
  };
};

// ─── empty note sentinel ────────────────────────────────────────────────────
const EMPTY_NOTE = {
  releaseNote: "",
  comments: "",
  cleared: false,
  clearanceNotes: "",
  clearanceDateTime: null,
  clearedByStaffID: "",
  releaseNoteID: "",
};

// ────────────────────────────────────────────────────────────────────────────

const BoookingsReleaseInformation = ({ startingPoint = "" }) => {
  const [form] = Form.useForm();
  const [releaseNotesForm] = Form.useForm();
  const [QForm] = Form.useForm();
  const dispatch = useDispatch();

  const booking = useSelector((s) => s.bookings?.bookings);
  const { bookingsFieldsEnabled } = useSelector((s) => s?.bookings);
  const fullSearch = useSelector((s) => s.fullSearch);
  const globalClientParam = useSelector((s) => s.config.globalClientParam);
  const currentUser = JSON.parse(localStorage.getItem("userDetails"));

  // "Config" — drag-reorder/resize columns, persisted per-user (see
  // docs/plans/grid-column-config-feature.md).
  const {
    headers: releaseNotesHeaders,
    isConfiguring,
    configButtonLabel,
    startConfiguring,
    confirmSave,
    discardChanges,
    dataTableRef,
  } = useGridColumnConfig("Bookings:ReleaseNotes", RELEASE_NOTES_HEADERS);

  // ── Sentence-To-Serve client params ──────────────────────────────────────
  // Legacy: ClientGetClientParam("Bookings", "UseSTS_Automatic", "False") or
  // ClientGetClientParam("Bookings", "UseSTS_Manual", "False") gates the
  // "Use STS?" checkbox. UseSTS_Manual additionally makes STS Days/Hours
  // editable by staff; without it (Automatic only) they're read-only and get
  // computed by Work Crews as the inmate works off hours.
  const useSTSAutomatic =
    String(
      checkGlobalClientParams("Bookings", "UseSTS_Automatic")?.paramValue ??
        "false",
    ).toLowerCase() === "true";
  const useSTSManual =
    String(
      checkGlobalClientParams("Bookings", "UseSTS_Manual")?.paramValue ??
        "false",
    ).toLowerCase() === "true";
  const showSTS = useSTSAutomatic || useSTSManual;

  // ── UI state ───────────────────────────────────────────────────────────
  const [isRowSelected, setIsRowSelected] = useState(false);
  const [overrideDateTime, setOverrideDateTime] = useState(false);
  const [stsUseChecked, setStsUseChecked] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [releaseNotesList, setReleaseNotesList] = useState([]);
  const [releaseNoteID, setReleaseNoteID] = useState(null);
  const [isQFormBtnEnable, setIsQFormBtnEnable] = useState(true);
  const [formData, setFormData] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [apiFields, setApiFields] = useState([]);
  const [selectedReleaseNotes, setSelectedReleaseNotes] = useState(EMPTY_NOTE);

  // ── Guard: suppress the form→Redux→booking→form loop ──────────────────
  // Set to true while we are programmatically setting form values so the
  // onFieldsChange handler knows not to dispatch back to Redux.
  const isSettingForm = useRef(false);

  // ── Security ────────────────────────────────────────────────────────────
  const canAdd = useMemo(
    () =>
      hasCheckSecurity(
        Security_Types.functionality,
        Entity_types.subModule,
        LABELS_CAPITAL.ADD,
        MODULES_LIST_WITH_ID.MODULE_BOOKINGS,
        SUB_MODULES_LIST_WITH_ID.Arrest_Report,
      ),
    [],
  );
  const canView = useMemo(
    () =>
      hasCheckSecurity(
        Security_Types.functionality,
        Entity_types.subModule,
        LABELS_CAPITAL.VIEW,
        MODULES_LIST_WITH_ID.MODULE_BOOKINGS,
        SUB_MODULES_LIST_WITH_ID.Arrest_Report,
      ),
    [],
  );
  const canModify = useMemo(
    () =>
      hasCheckSecurity(
        Security_Types.functionality,
        Entity_types.subModule,
        LABELS_CAPITAL.MODIFY,
        MODULES_LIST_WITH_ID.MODULE_BOOKINGS,
        SUB_MODULES_LIST_WITH_ID.Arrest_Report,
      ),
    [],
  );
  const canDelete = useMemo(
    () =>
      hasCheckSecurity(
        Security_Types.functionality,
        Entity_types.subModule,
        LABELS_CAPITAL.DELETE,
        MODULES_LIST_WITH_ID.MODULE_BOOKINGS,
        SUB_MODULES_LIST_WITH_ID.Arrest_Report,
      ),
    [],
  );

  // ── Dynamic extra fields ─────────────────────────────────────────────────
  const dynamicFields = useMemo(() => {
    if (!apiFields?.length) return [];
    return apiFields.map((f) => ({
      ...f,
      label: f.label || f.columnName,
      type: f.type || "varchar",
    }));
  }, [apiFields]);

  // ── Fetch extra-field definitions once ──────────────────────────────────
  useEffect(() => {
    if (!globalClientParam) return;
    api
      .get(API_ENDPOINTS.getBookingExtra)
      .then(({ data }) => {
        const paramContexts = globalClientParam
          .filter(
            (p) =>
              p.paramName === "ArrestReportsExtra" &&
              p.paramValue === "ReleaseInformation",
          )
          .map((p) => p.paramContext);

        const filtered = (data || []).filter(
          (item) =>
            item?.columnName?.endsWith("_U") &&
            paramContexts.includes(item.columnName),
        );
        setApiFields(convertApiFields(filtered, "ArrestReportsExtra"));
      })
      .catch(console.error);
  }, [globalClientParam]);

  // ── Sync Redux booking → local formData ─────────────────────────────────
  // When the scheduled release date is derived from the controlling charge
  // (not yet stored in arrestReports), we write it back into Redux exactly
  // once so every other subscriber sees the resolved value.
  // The isSettingForm guard ensures this dispatch does NOT re-trigger the
  // form's onFieldsChange -> updateArrestReport path.
  useEffect(() => {
    const {
      formValues,
      chargesDerivedDate,
      chargesDerivedTime,
      chargesDerivedReleaseReasonId,
    } = buildFormData(booking, apiFields);

    setFormData(formValues);

    // Sync charge-derived scheduled release / release reason back into
    // arrestReports[0] — covers both Priority 1 (an active sentence) and
    // Priority 2 (the Controlling Charge just got cleared, e.g. dismissed)
    // from buildFormData above.
    const hasDerivedReleaseReason = isNonEmptyVariable(
      chargesDerivedReleaseReasonId,
    );
    if (
      (chargesDerivedDate || hasDerivedReleaseReason) &&
      booking?.arrestReports?.length > 0
    ) {
      const ar = booking.arrestReports[0];
      const arPatch = { ...ar };
      if (chargesDerivedDate) {
        arPatch.scheduledReleaseDate = chargesDerivedDate;
        arPatch.scheduledReleaseTime = chargesDerivedTime;
      }
      if (hasDerivedReleaseReason) {
        arPatch.releaseReasonId = chargesDerivedReleaseReasonId;
      }
      dispatch(setBookings({ arrestReports: [arPatch] }));
    }
  }, [booking, apiFields]);

  // ── Sync Redux releaseNotes → local list ────────────────────────────────
  useEffect(() => {
    if (booking?.releaseNotes) {
      setReleaseNotesList(booking.releaseNotes.map((item) => ({ ...item })));
    }
  }, [booking?.releaseNotes]);

  // ── Push formData into the AntD form instance ───────────────────────────
  // The isSettingForm guard prevents onFieldsChange from firing a Redux
  // dispatch while we are merely reflecting Redux state into the form.
  useEffect(() => {
    isSettingForm.current = true;
    if (formData !== null) {
      form.setFieldsValue(formData);
      setOverrideDateTime(!!formData.scheduledReleaseOverridden);
      setStsUseChecked(!!formData.stsUse);
    } else {
      form.resetFields();
    }
    // Allow React to flush before we re-arm the guard
    const id = setTimeout(() => {
      isSettingForm.current = false;
    }, 0);
    return () => clearTimeout(id);
  }, [formData]);

  // ── Push selectedReleaseNotes into the modal form ────────────────────────
  useEffect(() => {
    if (selectedReleaseNotes) {
      releaseNotesForm.setFieldsValue(selectedReleaseNotes);
    }
  }, [selectedReleaseNotes]);

  // ── Disable Save button in edit mode when user lacks Modify permission ──
  useEffect(() => {
    setIsDisabled(isEdit ? !canModify : false);
  }, [isEdit, canModify]);

  // ── Redux dispatch helper ─────────────────────────────────────────────
  // Merges changed fields back into arrestReports[0] without touching the
  // rest of the booking tree. Only called from user-driven form changes.
  const updateArrestReport = useCallback(
    (changedData) => {
      const ar = booking?.arrestReports?.[0] ?? {};
      const extraFieldsData = {};
      apiFields?.forEach((field) => {
        extraFieldsData[field.columnName] =
          changedData?.[field.columnName] ?? null;
      });

      const payload = {
        arrestReports: [
          {
            ...ar,
            ...changedData,
            arrestReportsExtra: [
              {
                ...ar.arrestReportsExtra?.[0],
                ...extraFieldsData,
              },
            ],
          },
        ],
      };

      // Matches PCI: once a release date is set, the person is no longer
      // in custody, so the In Jail flag is removed rather than left active.
      if (isNonEmptyVariable(changedData?.releaseDate)) {
        const { cdInvolvements: filtered, removed } = removeInJailFlag(
          booking?.cdInvolvements,
          booking?.bookingId,
        );
        payload.cdInvolvements = filtered;

        // Rows already persisted need an actual DELETE so they don't come
        // back on reload; a same-session row with no invId yet was never
        // saved, so dropping it from the payload above is enough.
        removed.forEach((item) => {
          if (isNonEmptyVariable(item.invId)) {
            dispatch(setDeleteAPis(API_ENDPOINTS.jacketFlags + item.invId));
          }
        });
      }

      dispatch(setBookings(payload));
    },
    [dispatch, booking?.arrestReports, booking?.cdInvolvements, booking?.bookingId, apiFields],
  );

  // ── Debounced handler for main form changes ──────────────────────────────
  // Skipped entirely when isSettingForm is true so we never dispatch while
  // reflecting incoming Redux state.
  const dataModify = useMemo(
    () =>
      debounce(() => {
        if (isSettingForm.current) return;

        const values = form.getFieldsValue();
        setOverrideDateTime(!!values?.scheduledReleaseOverridden);
        setStsUseChecked(!!values?.stsUse);

        const fmtDateTime = (v) =>
          v ? dayjs(v).format("MM/DD/YYYY HH:mm") : null;
        const fmtDate = (v) => (v ? dayjs(v).format("MM/DD/YYYY") : null);

        const scheduledReleaseFmt = fmtDateTime(
          values.scheduledReleaseDateTime,
        );
        const releasedFmt = fmtDateTime(values.releasedDateTime);

        const data = {
          ...values,
          scheduledReleaseDate: scheduledReleaseFmt?.split(" ")[0] ?? null,
          scheduledReleaseTime: scheduledReleaseFmt?.split(" ")[1] ?? null,
          releaseDate: releasedFmt?.split(" ")[0] ?? null,
          releaseTime: releasedFmt?.split(" ")[1] ?? null,
          releaseWarCheckDate: fmtDate(values.releaseWarCheckDate),
        };

        // Sentence-To-Serve: when there's an eligible (sentenced,
        // non-cleared) charge, buildFormData already applies the STS credit
        // fresh from that charge's own date on every render (display-only,
        // never persisted here) — nothing to do. Without one, arrestReports.
        // scheduledReleaseDate is the only stored source, so — mirroring
        // legacy's STSChanged()/CalculateAllChargeReleaseDates — it's
        // maintained here as the live, credit-adjusted value directly: the
        // pre-credit date is snapshotted into stsScheduledReleaseDate the
        // first time credit applies (never overwritten after), and
        // scheduledReleaseDate is always re-derived as
        // stsScheduledReleaseDate minus the current (absolute) stsDays —
        // not incrementally, so retyping STS Days can't compound. Skipped
        // entirely when the release date is manually overridden, matching
        // CalculateOutDate's immediate exit on Scheduled_Release_Overridden.
        const overridden = Boolean(values?.scheduledReleaseOverridden);
        const hasChargeDerivedRelease =
          !overridden &&
          booking?.charges?.some(
            (c) =>
              (c.sentenced === true &&
                c.clearedToGo !== true &&
                isNonEmptyVariable(c?.scheduledReleaseDate)) ||
              // Priority 2 from buildFormData above — a cleared Controlling
              // Charge's Clearance Date is the release-date source; STS
              // credit doesn't apply to a dismissal, so it's excluded here
              // the same way an active sentence is.
              (c.controllingCharge === true &&
                c.clearedToGo === true &&
                isNonEmptyVariable(c?.clearanceDate)),
          );

        if (!hasChargeDerivedRelease && !overridden) {
          const ar = booking?.arrestReports?.[0];
          let stsScheduledReleaseDate = ar?.stsScheduledReleaseDate ?? null;
          let stsScheduledReleaseTime = ar?.stsScheduledReleaseTime ?? null;
          const stsDaysNum = isNonEmptyVariable(values?.stsDays)
            ? Number(values.stsDays)
            : 0;

          if (values?.stsUse && stsDaysNum > 0) {
            if (!stsScheduledReleaseDate) {
              // First time credit applies this session — snapshot the
              // pre-credit date/time exactly as it stands right now.
              stsScheduledReleaseDate =
                data.scheduledReleaseDate ?? ar?.scheduledReleaseDate ?? null;
              stsScheduledReleaseTime =
                data.scheduledReleaseTime ?? ar?.scheduledReleaseTime ?? null;
            }
            if (stsScheduledReleaseDate) {
              data.scheduledReleaseDate = dayjs(
                stsScheduledReleaseDate,
                "MM/DD/YYYY",
              )
                .subtract(stsDaysNum, "day")
                .format("MM/DD/YYYY");
              data.scheduledReleaseTime = stsScheduledReleaseTime;
            }
          } else if (stsScheduledReleaseDate) {
            // STS turned off (or Days cleared) — restore the pre-credit date.
            data.scheduledReleaseDate = stsScheduledReleaseDate;
            data.scheduledReleaseTime = stsScheduledReleaseTime;
          }
          data.stsScheduledReleaseDate = stsScheduledReleaseDate;
          data.stsScheduledReleaseTime = stsScheduledReleaseTime;
        }

        // Format dynamic extra fields
        apiFields?.forEach(({ columnName, type }) => {
          data[columnName] = values?.[columnName]
            ? type === "date"
              ? fmtDate(values[columnName])
              : type === "datetime"
                ? fmtDateTime(values[columnName])
                : values[columnName]
            : null;
        });

        // Remove the combined datetime keys — only split keys go to the API
        delete data.scheduledReleaseDateTime;
        delete data.releasedDateTime;

        updateArrestReport(data);
      }, 600),
    // booking is read directly here (charges/arrestReports, for the STS
    // block above) in addition to via updateArrestReport, so it has to be
    // listed on its own — updateArrestReport's own deps don't cover
    // booking.charges changing on its own (e.g. a Charge's sentence is
    // edited without arrestReports also changing).
    [form, apiFields, updateArrestReport, booking],
  );

  // ── Release Notes helpers ────────────────────────────────────────────────
  const clickAddModalAction = useCallback(() => {
    setShowAddModal((v) => !v);
    setSelectedReleaseNotes(null);
    setIsRowSelected(false);
    setIsEdit(false);
  }, []);

  const addNewNotes = useCallback(() => {
    setReleaseNoteID(null);
    releaseNotesForm.setFieldsValue({
      releaseNote: "",
      comments: "",
      cleared: false,
      clearanceNotes: "",
      clearanceDateTime: null,
      clearedByStaffID: null,
    });
    setSelectedReleaseNotes(null);
    setIsRowSelected(false);
    clickAddModalAction();
  }, [releaseNotesForm, clickAddModalAction]);

  const patchSelectedRow = useCallback(
    (editData) => {
      const selectedNotes = {
        releaseNote: editData?.releaseNote,
        comments: editData?.comments,
        cleared: editData?.cleared === "Yes",
        clearanceNotes: editData?.clearanceNotes,
        clearanceDateTime: editData?.clearanceDate
          ? dayjs(`${editData.clearanceDate} ${editData.clearanceTime}`)
          : null,
        clearedByStaffID: editData?.clearedByStaffID,
      };
      releaseNotesForm.setFieldsValue(selectedNotes);
      setReleaseNoteID(editData?.releaseNoteID || editData?.id);
      setSelectedReleaseNotes(selectedNotes);
      setIsRowSelected(true);
    },
    [releaseNotesForm],
  );

  const persistNotesList = useCallback(
    (updatedList) => {
      setReleaseNotesList(updatedList);
      dispatch(setBookings({ releaseNotes: updatedList }));
    },
    [dispatch],
  );

  const saveReleaseNts = useCallback(() => {
    releaseNotesForm.validateFields().then((values) => {
      const updatedList = releaseNoteID
        ? releaseNotesList.map((p) =>
            p.releaseNoteID === releaseNoteID || p.id === releaseNoteID
              ? { ...values, releaseNoteID }
              : p,
          )
        : [...releaseNotesList, { ...values, id: uuidv4() }];

      persistNotesList(updatedList);
      clickAddModalAction();
    });
  }, [
    releaseNotesForm,
    releaseNoteID,
    releaseNotesList,
    persistNotesList,
    clickAddModalAction,
  ]);

  const QFormDataSave = useCallback(() => {
    QForm.validateFields().then((values) => {
      persistNotesList([...releaseNotesList, { ...values, id: uuidv4() }]);
      QForm.resetFields();
    });
  }, [QForm, releaseNotesList, persistNotesList]);

  const deleteReleaseNotes = useCallback(() => {
    if (!releaseNoteID) return;

    const updatedList = releaseNotesList.filter(
      (item) =>
        item?.releaseNoteID !== releaseNoteID && item?.id !== releaseNoteID,
    );
    persistNotesList(updatedList);
    dispatch(
      setDeleteAPis(`${API_ENDPOINTS.releaseInformation}/id/${releaseNoteID}`),
    );
    setReleaseNoteID(null);
    setIsRowSelected(false);
    setSelectedReleaseNotes(EMPTY_NOTE);
  }, [releaseNoteID, releaseNotesList, persistNotesList, dispatch]);

  // ── Derived: table rows with "Yes"/"No" cleared label ───────────────────
  const tableData = useMemo(
    () =>
      (releaseNotesList || []).map((item) => ({
        ...item,
        cleared: item?.cleared ? "Yes" : "No",
      })),
    [releaseNotesList],
  );

  // ────────────────────────────────────────────────────────────────────────
  // Render
  // ────────────────────────────────────────────────────────────────────────
  return (
    <>
      {/* ── Main Release Information Form ──────────────────────────── */}
      <FieldSecurityProvider
        entity={SCHEMA_NAME.ARREST_REPORT}
        startingPoint={startingPoint}
        bookingsFieldsEnabled={bookingsFieldsEnabled}
      >
        <Form
          form={form}
          initialValues={formData}
          disabled={!bookingsFieldsEnabled}
          onFieldsChange={dataModify}
        >
          <StyledSectionHeading marginTop="-10px" marginBottom="0px">
            <span>Release Information</span>
          </StyledSectionHeading>

          <StyledGridRow justify="end">
            <StyledGridCol>
              <SecureFormItem
                fieldName="scheduledReleaseOverridden"
                formItemProps={{
                  name: "scheduledReleaseOverridden",
                  label: "Override release date?",
                  valuePropName: "checked",
                  layout: "horizontal",
                  labelPostion: "label-right",
                }}
              >
                <Checkbox
                  id="isOverridereleaseDate"
                  name="isOverridereleaseDate"
                />
              </SecureFormItem>
            </StyledGridCol>

            {showSTS && (
              <StyledGridCol>
                <SecureFormItem
                  fieldName="stsUse"
                  formItemProps={{
                    name: "stsUse",
                    label: "Use STS?",
                    valuePropName: "checked",
                    layout: "horizontal",
                    labelPostion: "label-right",
                  }}
                >
                  <Checkbox id="RI-UseSTS" name="stsUse" />
                </SecureFormItem>
              </StyledGridCol>
            )}
          </StyledGridRow>

          <StyledGridRow gutter={16}>
            {/* Scheduled Release — disabled when override is off */}
            <StyledGridCol lg={8}>
              <SecureFormItem
                fieldName="scheduledReleaseDateTime"
                overrideDisabled={!overrideDateTime}
                formItemProps={{
                  name: "scheduledReleaseDateTime",
                  label: "Scheduled Release Date And Time",
                }}
              >
                <CommonDatePicker
                  format="MM/DD/YYYY hh:mm"
                  placeholder="Select Date and Time"
                />
              </SecureFormItem>
            </StyledGridCol>

            {/* Release Date And Time */}
            <StyledGridCol lg={6}>
              <SecureFormItem
                fieldName="releasedDateTime"
                formItemProps={{
                  name: "releasedDateTime",
                  label: "Release Date And Time",
                }}
              >
                <CommonDatePicker
                  disableFutureDates
                  format="MM/DD/YYYY HH:mm"
                  placeholder="Select Date and Time"
                />
              </SecureFormItem>
            </StyledGridCol>

            {/* Released By */}
            <StyledGridCol lg={5}>
              <SecureFormItem
                fieldName="releaseOfficerStaffId"
                formItemProps={{
                  name: "releaseOfficerStaffId",
                  label: "Released By",
                }}
              >
                <Select
                  id="RI-ReleasedBy"
                  options={getOptionsFromRefList("", true, false, true)}
                  placeholder="Released By"
                  showSearch
                  variant="outlined"
                />
              </SecureFormItem>
            </StyledGridCol>

            {/* Release Reason */}
            <StyledGridCol lg={6}>
              <SecureFormItem
                fieldName="releaseReasonId"
                formItemProps={{
                  name: "releaseReasonId",
                  label: "Release Reason",
                }}
              >
                <Select
                  id="RI-ReleaseReason"
                  options={getOptionsFromRefList(
                    "ChargeClearance",
                    false,
                    false,
                    true,
                  )}
                  onChange={() => {
                    form.setFieldValue("releaseToAuthorityId", null);
                    form.setFieldValue("releaseToFacilityId", null);
                    form.setFieldValue("releaseToState", null);
                  }}
                  placeholder="Release Reason"
                  showSearch
                  variant="outlined"
                />
              </SecureFormItem>
            </StyledGridCol>

            {/* Conditional fields based on release reason */}
            {formData?.releaseReasonId == 54240015 && (
              <StyledGridCol lg={6}>
                <SecureFormItem
                  fieldName="releaseToAuthorityId"
                  formItemProps={{
                    name: "releaseToAuthorityId",
                    label: "Release to (Authority)",
                  }}
                >
                  <Select
                    id="RI-ReleaseToAuthorityId"
                    options={getOptionsFromRefList(
                      "Release_to_Authority",
                      false,
                      false,
                      true,
                    )}
                    placeholder="Release to Authority"
                    showSearch
                    variant="outlined"
                  />
                </SecureFormItem>
              </StyledGridCol>
            )}

            {formData?.releaseReasonId == 54240016 && (
              <StyledGridCol>
                <SecureFormItem
                  fieldName="releaseToFacilityId"
                  formItemProps={{
                    name: "releaseToFacilityId",
                    label: "Release to (Facility)",
                  }}
                >
                  <Select
                    id="RI-ReleaseToFacilityId"
                    options={getOptionsFromRefList(
                      "Release_to_Facility",
                      false,
                      false,
                      true,
                    )}
                    placeholder="Release to Facility"
                    showSearch
                    variant="outlined"
                  />
                </SecureFormItem>
              </StyledGridCol>
            )}

            {formData?.releaseReasonId == 54240017 && (
              <StyledGridCol>
                <SecureFormItem
                  fieldName="releaseToState"
                  formItemProps={{
                    name: "releaseToState",
                    label: "Release to (State)",
                  }}
                >
                  <Select
                    id="RI-ReleaseToState"
                    options={getOptionsFromRefList("USState")}
                    placeholder="Release to State"
                    showSearch
                    variant="outlined"
                  />
                </SecureFormItem>
              </StyledGridCol>
            )}

            {/* Days Confined — always read-only */}
            <StyledGridCol>
              <SecureFormItem
                fieldName="totalDaysInCustody"
                overrideDisabled
                formItemProps={{
                  name: "totalDaysInCustody",
                  label: "Total Days in Custody",
                }}
              >
                <TextField id="RI-DaysConfinedCurrentDetention" />
              </SecureFormItem>
            </StyledGridCol>

            {/* STS Days / STS Total Hours — only once "Use STS?" is checked.
                Read-only unless UseSTS_Manual is on; Automatic-only clients
                have these accrued by Work Crews as the inmate works hours. */}
            {showSTS && stsUseChecked && (
              <>
                <StyledGridCol>
                  <SecureFormItem
                    fieldName="stsDays"
                    overrideDisabled={!useSTSManual}
                    formItemProps={{ name: "stsDays", label: "STS Days" }}
                  >
                    <TextField id="RI-STSDays" type="number" />
                  </SecureFormItem>
                </StyledGridCol>

                <StyledGridCol>
                  <SecureFormItem
                    fieldName="stsHours"
                    overrideDisabled={!useSTSManual}
                    formItemProps={{
                      name: "stsHours",
                      label: "STS Total Hours",
                    }}
                  >
                    <TextField id="RI-STSHours" type="number" />
                  </SecureFormItem>
                </StyledGridCol>
              </>
            )}

            {/* Dynamic extra fields — ArrestReportsExtra columns routed to
                "ReleaseInformation" via client params. */}
            {dynamicFields.map((field, index) => (
              <StyledGridCol key={field.columnName ?? index}>
                <SecureFormItem
                  fieldName={field.columnName}
                  formItemProps={{ name: field.columnName, label: field.label }}
                >
                  {field.type === "date" ? (
                    <CommonDatePicker format="MM/DD/YYYY" showTime={false} />
                  ) : field.type === "datetime" ? (
                    <CommonDatePicker format="MM/DD/YYYY HH:mm" showTime />
                  ) : field.type === "bit" ? (
                    <Select
                      options={getOptionsFromRefList(field.refListName)}
                    />
                  ) : (
                    <TextField />
                  )}
                </SecureFormItem>
              </StyledGridCol>
            ))}
          </StyledGridRow>
        </Form>
      </FieldSecurityProvider>

      {/* ── Release Notes — Quick-Add Form ─────────────────────────── */}
      <StyledSectionHeading>
        <span>Release Notes</span>
      </StyledSectionHeading>

      <FieldSecurityProvider
        entity="ReleaseNotes"
        startingPoint={startingPoint}
        bookingsFieldsEnabled={bookingsFieldsEnabled}
      >
        <Form
          form={QForm}
          initialValues={{ releaseNote: "", comments: "" }}
          disabled={!bookingsFieldsEnabled}
          onFieldsChange={() => {
            setIsQFormBtnEnable(!QForm.getFieldValue("releaseNote")?.length);
          }}
        >
          <StyledGridRow gutter={16}>
            <StyledGridCol span={8}>
              <SecureFormItem
                fieldName="releaseNote"
                formItemProps={{ name: "releaseNote", label: "Release Note" }}
              >
                <TextField id="RI-QAdd-ReleaseNote" />
              </SecureFormItem>
            </StyledGridCol>
            <StyledGridCol span={8}>
              <SecureFormItem
                fieldName="comments"
                formItemProps={{ name: "comments", label: "Comments" }}
              >
                <TextField id="RI-QAdd-Comments" />
              </SecureFormItem>
            </StyledGridCol>
            <StyledGridCol>
              <StyledFlex
                align="center"
                style={{ height: "100%", paddingTop: "10px" }}
              >
                <Button
                  color="primary"
                  type="primary"
                  onClick={QFormDataSave}
                  disabled={
                    !bookingsFieldsEnabled || isQFormBtnEnable || !canAdd
                  }
                >
                  Q-ADD
                </Button>
              </StyledFlex>
            </StyledGridCol>
          </StyledGridRow>
        </Form>

        {/* ── Release Notes Table + Toolbar ──────────────────────────── */}
        <InmateDetailsTableContainer>
          <TableHeader>
            <div />
            <div>
              <Button
                color="primary"
                type="primary"
                onClick={addNewNotes}
                disabled={!bookingsFieldsEnabled || isConfiguring || !canAdd}
              >
                Add
              </Button>
              <Button
                color="primary"
                type="primary"
                style={{ marginLeft: "15px" }}
                disabled={!isRowSelected || isConfiguring || !canView}
                onClick={() => {
                  setShowAddModal(true);
                  setIsEdit(true);
                }}
              >
                Details
              </Button>
              <Popconfirm
                title="Delete the Release Information"
                description="Are you sure to delete this Release Information?"
                onConfirm={deleteReleaseNotes}
                onCancel={() => {}}
                okText="Yes"
                cancelText="No"
              >
                <Button
                  color="primary"
                  type="primary"
                  style={{ marginLeft: "10px" }}
                  disabled={
                    !bookingsFieldsEnabled ||
                    !isRowSelected ||
                    isConfiguring ||
                    releaseNotesList?.length === 0 ||
                    !canDelete
                  }
                >
                  Delete
                </Button>
              </Popconfirm>

              {/* Config — drag-reorder/resize columns; only while Modify is active */}
              {!isConfiguring ? (
                <Button
                  color="primary"
                  type="primary"
                  style={{ marginLeft: "10px" }}
                  htmlType="button"
                  onClick={startConfiguring}
                >
                  {configButtonLabel}
                </Button>
              ) : (
                <Popconfirm
                  title="Do you want to save the current list view configuration?"
                  onConfirm={confirmSave}
                  onCancel={discardChanges}
                  okText="Yes"
                  cancelText="No"
                >
                  <Button
                    color="primary"
                    type="primary"
                    style={{ marginLeft: "10px" }}
                    htmlType="button"
                  >
                    {configButtonLabel}
                  </Button>
                </Popconfirm>
              )}
            </div>
          </TableHeader>

          {canView && (
            <Table
              ref={dataTableRef}
              headers={releaseNotesHeaders}
              draggable={isConfiguring}
              data={tableData}
              onRowClick={patchSelectedRow}
              onRowDoubleClick={(data) => {
                patchSelectedRow(data);
                setShowAddModal(true);
                setIsEdit(true);
              }}
              selected={selectedReleaseNotes === null}
            />
          )}
        </InmateDetailsTableContainer>

        {/* ── Add / Edit Release Note Modal ──────────────────────────── */}
        <Modal
          header="Add Release Note"
          show={showAddModal}
          onHide={clickAddModalAction}
          footerActions={[
            {
              key: "save",
              text: "Save",
              onClick: saveReleaseNts,
              disabled: isDisabled || !bookingsFieldsEnabled,
            },
          ]}
          footerCloseAction={() => setIsEdit(false)}
          footerCloseActionText="Cancel"
        >
          <Form form={releaseNotesForm} initialValues={selectedReleaseNotes}>
            <StyledGridRow>
              <StyledGridCol span={24}>
                <SecureFormItem
                  fieldName="releaseNote"
                  formItemProps={{
                    name: "releaseNote",
                    label: "Release Note",
                    required: !fullSearch,
                    rules: !fullSearch
                      ? [
                          {
                            required: true,
                            message: "Release Note is required",
                          },
                        ]
                      : [],
                  }}
                >
                  <TextField />
                </SecureFormItem>
              </StyledGridCol>

              <StyledGridCol span={24}>
                <SecureFormItem
                  fieldName="comments"
                  formItemProps={{ name: "comments", label: "Comment" }}
                >
                  <TextField inputType="textarea" />
                </SecureFormItem>
              </StyledGridCol>
            </StyledGridRow>

            <StyledSectionHeading>
              <span>Clearance Information</span>
            </StyledSectionHeading>

            <StyledGridRow gutter={16}>
              <StyledGridCol span={24}>
                <SecureFormItem
                  fieldName="cleared"
                  formItemProps={{
                    name: "cleared",
                    label: "Cleared?",
                    valuePropName: "checked",
                    layout: "horizontal",
                    className: "form-item-checkbox-left",
                  }}
                >
                  <Checkbox
                    id="release-notes-cleared"
                    name="cleared"
                    checked={selectedReleaseNotes?.cleared}
                    onChange={(e) => {
                      setSelectedReleaseNotes({
                        releaseNote:
                          releaseNotesForm.getFieldValue("releaseNote"),
                        comments: releaseNotesForm.getFieldValue("comments"),
                        clearedByStaffID: currentUser?.staffId,
                        clearanceNotes: null,
                        clearanceDateTime: dayjs(),
                        cleared: e.target.checked,
                      });
                    }}
                  />
                </SecureFormItem>
              </StyledGridCol>

              {selectedReleaseNotes?.cleared && (
                <>
                  <StyledGridCol span={12}>
                    <SecureFormItem
                      fieldName="clearanceDateTime"
                      formItemProps={{
                        name: "clearanceDateTime",
                        label: "Date / Time",
                      }}
                    >
                      <CommonDatePicker format="MM/DD/YYYY hh:mm" />
                    </SecureFormItem>
                  </StyledGridCol>

                  <StyledGridCol span={12}>
                    <SecureFormItem
                      fieldName="clearedByStaffID"
                      formItemProps={{
                        name: "clearedByStaffID",
                        label: "Cleared By",
                      }}
                    >
                      <Select
                        options={getOptionsFromRefList("", true, false, true)}
                        placeholder="Cleared By"
                        showSearch
                        variant="outlined"
                        onChange={() =>
                          setTimeout(() => document.activeElement.blur(), 0)
                        }
                      />
                    </SecureFormItem>
                  </StyledGridCol>

                  <StyledGridCol span={24}>
                    <SecureFormItem
                      fieldName="clearanceNotes"
                      formItemProps={{
                        name: "clearanceNotes",
                        label: "Clearance Notes",
                      }}
                    >
                      <TextField inputType="textarea" />
                    </SecureFormItem>
                  </StyledGridCol>
                </>
              )}
            </StyledGridRow>
          </Form>
        </Modal>
      </FieldSecurityProvider>
    </>
  );
};

export default BoookingsReleaseInformation;
